import AsyncStorage from '@react-native-async-storage/async-storage';
import { axiosinstance } from '../axiosClient';
import useAuth from './useAuth';
import { useCallback } from 'react';
import type { RefreshOutcome } from '../utils/refreshManager';
import { clearAuthFailure } from '../utils/attachAuthInterceptors';
import { useAppSelector } from '../redux/helper';
import { getEmployeePassword } from '../services/SecureCredentials';

const useRefresh = () => {
  const { auth, setAuth } = useAuth();
  const employeePhone = useAppSelector(
    s => s.auth.inApp.account.employeeInfo?.bp_employee_phone_number,
  );
  const refresh = useCallback(async (): Promise<RefreshOutcome> => {
    try {
      const refreshToken = await AsyncStorage.getItem('refreshToken');
      if (!refreshToken) {
        return { kind: 'rejected' };
      }
      let response: any;
      try {
        response = await axiosinstance.post(
          `bp_refresh_token_information/${refreshToken}`,
          undefined,
          {
            headers: { Authorization: 'Bearer ' + auth?.accessToken },
          },
        );
      } catch (error: any) {
        if (!error?.response) return { kind: 'unreachable' };
        // Server rejected the refresh token — fall through to the
        // stored-credentials re-login below.
      }

      // Fallback: re-login with stored credentials (as Back Office does) so a
      // rejected refresh token doesn't log the user out.
      if (response?.data?.status !== 200 && employeePhone) {
        const password = await getEmployeePassword().catch(() => null);
        if (password) {
          try {
            response = await axiosinstance.post(
              'bp_employee_login_information',
              JSON.stringify({
                bp_phone_number: employeePhone,
                bp_password: password,
              }),
            );
          } catch (error: any) {
            if (!error?.response) return { kind: 'unreachable' };
          }
        }
      }

      // Login can answer 200 without tokens (e.g. "New verification code
      // sent"); only accept a response that carries a refresh token.
      if (response?.data?.status === 200 && response?.data?.refreshToken) {
        setAuth({
          accessToken: response?.data?.accessToken,
          refreshToken: response?.data?.refreshToken,
        });
        if (response?.data?.accessToken) {
          await AsyncStorage.setItem('accessToken', response.data.accessToken);
        }
        await AsyncStorage.setItem(
          'refreshToken',
          response?.data?.refreshToken,
        );
        clearAuthFailure('pos');
        return { kind: 'success', accessToken: response.data.accessToken };
      } else {
        return { kind: 'rejected' };
      }
    } catch (error: any) {
      if (error?.response) {
        return { kind: 'rejected' };
      }
      return { kind: 'unreachable' };
    }
  }, [auth?.accessToken, employeePhone, setAuth]);
  return refresh;
};

export default useRefresh;
